import { useState } from 'react'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { FormItem, Form } from '@/components/ui/Form'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { ZodType } from 'zod'
import type { CommonProps } from '@/@types/common'
import { useEmailSubscription } from '@/api/subscription/useEmailSubscription'
import CaptchaField from '@/components/form/fields/CaptchaField'

interface SubscribeFormProps extends CommonProps {
    onSuccess?: (email: string) => void
}

type SubscribeFormSchema = {
    email: string
    recaptcha?: string | null
}

const validationSchema: ZodType<SubscribeFormSchema> = z.object({
    email: z
        .string({ required_error: 'Please enter your email' })
        .min(1, { message: 'Please enter your email' })
        .email({ message: 'Please enter a valid email address' }),
    recaptcha: z.string().optional().nullable(),
})

const SubscribeForm = (props: SubscribeFormProps) => {
    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const { className, onSuccess } = props

    const {
        handleSubmit,
        formState: { errors },
        control,
        setError,
    } = useForm<SubscribeFormSchema>({
        defaultValues: {
            email: '',
            recaptcha: undefined,
        },
        resolver: zodResolver(validationSchema),
    })

    const { subscribe } = useEmailSubscription()

    const onSubscribe = async (values: SubscribeFormSchema) => {
        setSubmitting(true)

        try {
            const result = await subscribe({
                email: values.email,
                type: 'early_access',
                recaptcha: values.recaptcha,
            })

            if (result) {
                onSuccess?.(values.email)
            }
        } catch (error: any) {
            setError('email', {
                type: 'manual',
                message: error.message || 'Failed to subscribe. Please try again.',
            })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className={className}>
            <Form onSubmit={handleSubmit(onSubscribe)}>
                <FormItem
                    label="Email Address"
                    invalid={Boolean(errors.email)}
                    errorMessage={errors.email?.message}
                >
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="email"
                                placeholder="Enter your email"
                                autoComplete="email"
                                {...field}
                            />
                        )}
                    />
                </FormItem>

                {/* Captcha */}
                <CaptchaField name={'recaptcha'} control={control} className="my-4" />

                <Button
                    block
                    loading={isSubmitting}
                    variant="solid"
                    type="submit"
                >
                    {isSubmitting ? 'Subscribing...' : 'Notify Me'}
                </Button>
            </Form>
        </div>
    )
}

export default SubscribeForm

